import { action, observable, runInAction } from 'mobx'
import { getStore, store } from './stores-repository'
import { withSpinner } from './with-spinner'
import { RegistrationService } from '../protocol/sls-server-protocol/registration-service'
import { NextMiddleware, RequestError, Response } from '../protocol/base-http-service'
import { goToRoute } from '../utils/route-utils'
import { REGISTRATION_SUCCESS } from '../domain/routes'
import { Profile } from '../domain/profile'
import { CompletedForm } from '../domain/form'
import { components } from '../domain/entities'
import { SnackbarStore } from './snackbar-store'

export enum LinkVerificationResult {
    SUCCESS = 'success',
    ERROR = 'ERROR',
    VERIFIED = 'VERIFIED'
}

export enum UnsubscribeResult {
    SUCCESS = 'success',
    ERROR = 'ERROR',
    ALREADY = 'ALREADY'
}

@store
export class RegistrationStore {

    static storeName: string = 'registrationStore'

    @observable
    form: Profile = null

    @observable
    formVerification: components["schemas"]["Application"] = null

    @observable
    clientId: string = null

    @observable
    channelId: string = null

    @observable
    personalDataLink: string = null

    @observable
    editError: any = {}

    @observable
    codeObsolete: boolean = false

    codeObsoleteTimer: NodeJS.Timeout = null

    @observable
    linkVerificationResult: LinkVerificationResult = null

    @observable
    captchaToken: string = null

    @observable
    unsubscribeResult: UnsubscribeResult = null

    private snackbarStore: SnackbarStore = getStore<SnackbarStore>(SnackbarStore.storeName)
    private registrationService: RegistrationService = null

    init = (serverUrl: string): void => {
        this.registrationService = new RegistrationService(serverUrl, {})
    }

    @action
    openRegistrationForm = async ({ clientId, channelId }: { clientId: string, channelId: string }): Promise<void> => {
        const onlineForm: Profile = await withSpinner(this.registrationService.getRegistrationChannelsForm({ clientId, channelId }))

        runInAction(() => {
            this.form = onlineForm
            this.clientId = clientId
            this.channelId = channelId
            this.formVerification = null
        })
    }

    @action
    getPersonalDataLink = async ({ clientId }: { clientId: string }): Promise<void> => {
        const linkReturn: string = await withSpinner(this.registrationService.getPersonalDataLink({ clientId }))

        runInAction(() => {
            this.personalDataLink = linkReturn
        })
    }

    @observable
    setCodeObsolete = (value: boolean): void => {
        this.codeObsolete = value
    }

    @observable
    setEditError = (value: any): void => {
        this.editError = value
    }

    @observable
    setCaptchaToken = (token: string): void => {
        this.captchaToken = token
    }

    @action
    fillRegistrationForm = async (
        form: CompletedForm,
        shouldEndRegistrationCallback: (result: components["schemas"]["Application"]) => boolean
    ): Promise<components["schemas"]["Application"]> => {
        return withSpinner(this.registrationService.fillForm(
            {
                clientId: this.clientId,
                channelId: this.channelId,
                form,
                captchaToken: this.captchaToken,
            },
            {
                responseMiddlewares: [(response: Response, next: NextMiddleware) => {
                    if (response.status === 200) {
                        runInAction(() => {
                            this.formVerification = response.data
                            this.codeObsolete = false
                        })

                        // Если нам пришел код верификации, мы проверяем, является ли поле обязательным или заполненным.
                        // Если да то не заканчиваем регистрацию
                        const shouldEnd = shouldEndRegistrationCallback(response.data)
                        if (shouldEnd) {
                            this.endRegistration()
                        }
                    }
                }],
                errorMiddlewares: [(error: RequestError, next: NextMiddleware) => {
                    if (error.response.status === 400) {
                        const validationResults = error.response?.data?.fieldsValidation || {}
                        const errorMap = {}
                        Object.keys(validationResults).forEach(fieldId => {
                            const status = validationResults[fieldId] || 'OK'
                            if (status === 'CLIENT_IS_ACTIVE') {
                                errorMap[fieldId] = 'Покупатель с указанной картой  уже зарегистрирован в системе.'
                            } else if (status === 'BAD_CARD_FORMAT') {
                                errorMap[fieldId] = 'Неверный номер карты. Убедитесь, что номер карты введен корректно.'
                            } else if (status === 'PHONE_USED') {
                                errorMap[fieldId] = 'Указанный номер телефона уже закреплен за другим покупателем.'
                            } else if (status === 'EMAIL_USED') {
                                errorMap[fieldId] = 'Указанный e-mail уже закреплен за другим покупателем.'
                            } else if (status === 'PHONE_IS_NOT_VALID') {
                                errorMap[fieldId] = 'Неверный формат номера телефона'
                            } else if (status === 'EMAIL_IS_NOT_VALID') {
                                errorMap[fieldId] = 'Неверный формат e-mail.'
                            } else if (status !== 'OK') {
                                // На случай других ошибочных статусов
                                errorMap[fieldId] = 'Ошибка. Убедитесь, что значение введено корректно.'
                            }
                        })
                        this.setEditError(errorMap)
                        this.snackbarStore.show({
                            message: 'Анкета неверно заполнена',
                            variant: 'error',
                        })
                    } else if (error.response.status === 401) {
                        this.snackbarStore.show({
                            message: 'Код с картинки введен некорректно',
                            variant: 'error',
                        })
                        return
                    } else {
                        this.snackbarStore.show({
                            message: 'В процессе регистрации возникла ошибка. Обратитесь в службу поддержки магазина',
                            variant: 'error',
                        })
                    }
                }]
            }
        ))
    }

    endRegistration = (): void => {
        this.formVerification = null
        goToRoute(REGISTRATION_SUCCESS)
    }

    sendVerificationCode = async (code: string): Promise<void> => {
        return withSpinner(this.registrationService.sendVerificationCode(
            {
                clientId: this.clientId,
                applicationId: this.formVerification.id,
                fieldId: this.formVerification.verificationField?.id,
                code,
                captchaToken: this.captchaToken,
            },
            {
                responseMiddlewares: [(response: Response, next: NextMiddleware) => {
                    if (response.status === 200) {
                        this.endRegistration()
                    }
                }],
                errorMiddlewares: [(error: RequestError, next: NextMiddleware) => {
                    if (error.response.status === 400) {
                        if (this.codeObsolete) {
                            this.snackbarStore.show({
                                message: 'Код подтверждения устарел',
                                variant: 'error',
                            })
                            return
                        }
                        this.snackbarStore.show({
                            message: 'Неверный код подтверждения',
                            variant: 'error',
                        })
                        return
                    }
                    if (error.response.status === 404) {
                        this.snackbarStore.show({
                            message: 'Анкета не существует',
                            variant: 'error',
                        })
                        return
                    }
                    this.snackbarStore.show({
                        message: 'В процессе регистрации возникла ошибка. Обратитесь в службу поддержки магазина',
                        variant: 'error',
                    })
                }]
            }
        ))
    }

    checkVerificationLink = async (linkId: string): Promise<void> => {
        return withSpinner(this.registrationService.checkVerificationLink(
            linkId,
            {
                responseMiddlewares: [(response: Response, next: NextMiddleware) => {
                    runInAction(() => {
                        this.linkVerificationResult = LinkVerificationResult.SUCCESS
                    })
                }],
                errorMiddlewares: [(error: RequestError, next: NextMiddleware) => {
                    if (error.response.status === 409) {
                        runInAction(() => {
                            this.linkVerificationResult = LinkVerificationResult.VERIFIED
                        })
                        return
                    }
                    runInAction(() => {
                        this.linkVerificationResult = LinkVerificationResult.ERROR
                    })
                }]
            }
        ))
    }

    unsubscribeClient = async (clientId: string, encodedData: string): Promise<void> => {
        return withSpinner(this.registrationService.unsubscribeClient(
            clientId, encodedData,
            {
                responseMiddlewares: [(response: Response, next: NextMiddleware) => {
                    runInAction(() => {
                        this.unsubscribeResult = UnsubscribeResult.SUCCESS
                    })
                }],
                errorMiddlewares: [(error: RequestError, next: NextMiddleware) => {
                    if (error.response.status === 409) {
                        runInAction(() => {
                            this.unsubscribeResult = UnsubscribeResult.ALREADY
                        })
                        return
                    }
                    runInAction(() => {
                        this.unsubscribeResult = UnsubscribeResult.ERROR
                    })
                }]
            }
        ))
    }
}
